import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, User, Building2, Loader2, Search, Check, Plus, MapPin,
  Sparkles, ChevronRight, ChevronDown, ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { auth } from '../../../utils/auth';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const brregCache = new Map();

const CreateCaseModal = ({ 
  isOpen, 
  onClose, 
  prefillData,
  brief,
  attachments = [],
  onConfirm,
  isCreating = false,
  senderEmail = '',
  senderName = '',
  senderOrg = ''
}) => {
  if (!isOpen) return null;
  
  return (
    <CreateCaseModalInner
      onClose={onClose}
      prefillData={prefillData}
      brief={brief}
      attachments={attachments}
      onConfirm={onConfirm}
      isCreating={isCreating}
      senderEmail={senderEmail || brief?.sender_email || prefillData?.suggested_client_email || ''}
      senderName={senderName || brief?.sender_name || prefillData?.suggested_client_name || ''}
      senderOrg={senderOrg || brief?.sender_org || prefillData?.suggested_client_org || ''}
    />
  );
};

const CreateCaseModalInner = ({
  onClose,
  prefillData,
  brief,
  attachments = [],
  onConfirm,
  isCreating = false,
  senderEmail,
  senderName,
  senderOrg
}) => {
  const { t, i18n } = useTranslation(['inbox', 'common']);
  const isEnglish = i18n.language === 'en' || i18n.language?.startsWith('en');
  
  const [currentStep, setCurrentStep] = useState(1);
  
  // Client state
  const [clientMode, setClientMode] = useState('loading');
  const [matchedClients, setMatchedClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingMatches, setLoadingMatches] = useState(true);
  
  // New client
  const [newClientType, setNewClientType] = useState('COMPANY');
  const [newClientData, setNewClientData] = useState({
    person_name: senderName || '',
    email: senderEmail || '',
    phone: '',
    company_name: senderOrg || '',
    org_nr: '',
    address: '',
    city: '',
    postal_code: '',
    contact_name: senderName || '',
    contact_email: senderEmail || '',
    contact_phone: '',
    contact_position: ''
  });
  
  // Brreg
  const [brregSearch, setBrregSearch] = useState('');
  const [brregResults, setBrregResults] = useState([]);
  const [brregLoading, setBrregLoading] = useState(false);
  const debouncedSearch = useDebounce(brregSearch, 300);
  
  // Case
  const [caseData, setCaseData] = useState({
    title: prefillData?.suggested_topic || prefillData?.suggested_title || brief?.what_this_is?.substring(0, 80) || '',
    case_type: prefillData?.suggested_category || brief?.suggested_case_type || 'CONTRACT',
    description: prefillData?.suggested_description || brief?.what_this_is || '',
    priority: prefillData?.suggested_priority || brief?.priority || 'NORMAL',
    tags: prefillData?.suggested_tags || []
  });
  
  const [newTag, setNewTag] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const titleInputRef = useRef(null);
  const typeDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && currentStep === 2 && isCaseStepValid) {
        handleSubmit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, onClose]);

  useEffect(() => {
    if (currentStep === 2 && titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [currentStep]);

  // Load matches
  useEffect(() => {
    const fetchMatches = async () => {
      setLoadingMatches(true);
      try {
        const token = auth.getToken();
        const response = await axios.post(`${API}/clients/match`, {
          sender_name: senderName,
          sender_email: senderEmail,
          sender_org: senderOrg
        }, { headers: { Authorization: `Bearer ${token}` } });
        
        const matches = response.data?.matches || [];
        setMatchedClients(matches);
        setClientMode(matches.length > 0 ? 'select' : 'new');
        if (matches.length > 0) setSelectedClient(matches[0]);
      } catch (error) {
        setClientMode('new');
      } finally {
        setLoadingMatches(false);
      }
    };
    fetchMatches();
  }, [senderEmail, senderName, senderOrg]);

  // Brreg search
  useEffect(() => {
    const searchBrreg = async () => {
      if (debouncedSearch.length < 2) { setBrregResults([]); return; }
      const cacheKey = debouncedSearch.toLowerCase();
      if (brregCache.has(cacheKey)) { setBrregResults(brregCache.get(cacheKey)); return; }
      setBrregLoading(true);
      try {
        const response = await axios.get(`${API}/brreg/search`, { params: { q: debouncedSearch, size: 5 } });
        if (response.data.success) {
          brregCache.set(cacheKey, response.data.companies);
          setBrregResults(response.data.companies);
        }
      } catch (error) { console.error('Brreg error:', error); }
      finally { setBrregLoading(false); }
    };
    searchBrreg();
  }, [debouncedSearch]);

  const selectBrregCompany = (company) => {
    setNewClientData(prev => ({
      ...prev,
      company_name: company.navn || '',
      org_nr: company.organisasjonsnummer || '',
      address: company.forretningsadresse?.adresse?.[0] || '',
      postal_code: company.forretningsadresse?.postnummer || '',
      city: company.forretningsadresse?.poststed || ''
    }));
    setBrregSearch('');
    setBrregResults([]);
    toast.success(isEnglish ? `${company.navn} selected` : `${company.navn} valgt`);
  };

  const isClientStepValid = useMemo(() => {
    if (clientMode === 'select') return !!selectedClient;
    if (newClientType === 'PERSON') return newClientData.person_name.trim().length > 0;
    return newClientData.company_name.trim().length > 0;
  }, [clientMode, selectedClient, newClientType, newClientData]);

  const isCaseStepValid = useMemo(() => caseData.title.trim().length > 0, [caseData.title]);

  const handleSubmit = () => {
    const payload = {
      ...caseData,
      client_id: clientMode === 'select' ? selectedClient?.id : null,
      new_client: clientMode === 'new' ? {
        type: newClientType,
        ...(newClientType === 'PERSON' ? {
          person_name: newClientData.person_name,
          email: newClientData.email || null,
          phone: newClientData.phone || null
        } : {
          company_name: newClientData.company_name,
          org_nr: newClientData.org_nr || null,
          address: newClientData.address || null,
          city: newClientData.city || null,
          postal_code: newClientData.postal_code || null,
          contacts: [{
            id: Date.now().toString(),
            name: newClientData.contact_name,
            email: newClientData.contact_email || null,
            phone: newClientData.contact_phone || null,
            position: newClientData.contact_position || null,
            role: 'PRIMARY'
          }].filter(c => c.name)
        })
      } : null
    };
    onConfirm(payload);
  };

  const caseTypes = [
    { value: 'CONTRACT', label: isEnglish ? 'Contract' : 'Kontrakt' },
    { value: 'CORPORATE', label: isEnglish ? 'Corporate' : 'Selskapsrett' },
    { value: 'EMPLOYMENT', label: isEnglish ? 'Employment' : 'Arbeidsrett' },
    { value: 'DISPUTE', label: isEnglish ? 'Dispute' : 'Tvist' },
    { value: 'REAL_ESTATE', label: isEnglish ? 'Real Estate' : 'Eiendom' },
    { value: 'TAX', label: isEnglish ? 'Tax' : 'Skatt' },
    { value: 'IP', label: isEnglish ? 'IP' : 'Immaterialrett' },
    { value: 'OTHER', label: isEnglish ? 'Other' : 'Annet' }
  ];

  const selectedType = caseTypes.find(t => t.value === caseData.case_type);

  const getClientName = () => {
    if (clientMode === 'select' && selectedClient) return selectedClient.company_name || selectedClient.person_name;
    if (clientMode === 'new') return newClientType === 'PERSON' ? newClientData.person_name : newClientData.company_name;
    return '';
  };

  // Labels
  const labels = {
    newCase: isEnglish ? 'New case' : 'Ny sak',
    selectClient: isEnglish ? 'Select client' : 'Velg klient',
    caseDetails: isEnglish ? 'Case details' : 'Saksdetaljer',
    searchingClients: isEnglish ? 'Looking for clients...' : 'Leter etter klienter...',
    foundMatch: isEnglish ? 'Found a match' : 'Fant en match',
    foundMatches: isEnglish ? 'matches found' : 'matcher funnet',
    useExisting: isEnglish ? 'Use existing' : 'Bruk eksisterende',
    createNew: isEnglish ? 'Create new' : 'Opprett ny',
    noClientsFound: isEnglish ? 'No existing clients found' : 'Ingen eksisterende klienter funnet',
    person: isEnglish ? 'Person' : 'Person',
    company: isEnglish ? 'Company' : 'Bedrift',
    fullName: isEnglish ? 'Full name' : 'Fullt navn',
    email: isEnglish ? 'Email' : 'E-post',
    phone: isEnglish ? 'Phone' : 'Telefon',
    searchBrreg: isEnglish ? 'Search Norwegian business registry...' : 'Søk i Brønnøysundregistrene...',
    companyName: isEnglish ? 'Company name' : 'Bedriftsnavn',
    orgNr: isEnglish ? 'Org. number' : 'Org.nummer',
    contactPerson: isEnglish ? 'Contact person' : 'Kontaktperson',
    position: isEnglish ? 'Position' : 'Stilling',
    change: isEnglish ? 'Change' : 'Endre',
    matterTitle: isEnglish ? 'Case title' : 'Sakstittel',
    titlePlaceholder: isEnglish ? 'e.g. Contract review for GlobalCorp' : 'f.eks. Kontraktsgjennomgang for GlobalCorp',
    type: isEnglish ? 'Type' : 'Type',
    priority: isEnglish ? 'Priority' : 'Prioritet',
    description: isEnglish ? 'Description' : 'Beskrivelse',
    descPlaceholder: isEnglish ? 'Brief description of the matter...' : 'Kort beskrivelse av saken...',
    tags: isEnglish ? 'Tags' : 'Tagger',
    addTag: isEnglish ? 'Add tag' : 'Legg til tag',
    aiSuggested: isEnglish ? 'AI suggested' : 'AI-foreslått',
    attachmentsNote: isEnglish ? 'attachments will be added' : 'vedlegg vil bli lagt til',
    continue: isEnglish ? 'Continue' : 'Fortsett',
    back: isEnglish ? 'Back' : 'Tilbake',
    creating: isEnglish ? 'Creating...' : 'Oppretter...',
    createMatter: isEnglish ? 'Create case' : 'Opprett sak',
    low: isEnglish ? 'Low' : 'Lav',
    normal: isEnglish ? 'Normal' : 'Normal',
    high: isEnglish ? 'High' : 'Høy',
    urgent: isEnglish ? 'Urgent' : 'Haster'
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
        
        {/* Modal */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.98, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: 10 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Ultra minimal */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{labels.newCase}</h2>
                <p className="text-sm text-gray-400 mt-0.5">
                  {currentStep === 1 ? labels.selectClient : labels.caseDetails}
                </p>
              </div>
              <button 
                type="button" 
                onClick={onClose} 
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            
            {/* Progress bar - minimal */}
            <div className="flex gap-2 mt-5">
              <div className={`h-1 flex-1 rounded-full transition-colors ${currentStep >= 1 ? 'bg-gray-900' : 'bg-gray-200'}`} />
              <div className={`h-1 flex-1 rounded-full transition-colors ${currentStep >= 2 ? 'bg-gray-900' : 'bg-gray-200'}`} />
            </div>
          </div>
          
          {/* Content */}
          <div className="px-6 pb-6 max-h-[60vh] overflow-y-auto">
            <AnimatePresence mode="wait">
              {currentStep === 1 ? (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  {/* Loading state */}
                  {loadingMatches && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      <span className="ml-3 text-sm text-gray-500">{labels.searchingClients}</span>
                    </div>
                  )}
                  
                  {/* Matches found */}
                  {!loadingMatches && matchedClients.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">
                          {matchedClients.length} {matchedClients.length > 1 ? labels.foundMatches : labels.foundMatch}
                        </span>
                      </div>
                      
                      {/* Toggle */}
                      <div className="flex p-1 bg-gray-100 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setClientMode('select')}
                          className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                            clientMode === 'select' 
                              ? 'bg-white text-gray-900 shadow-sm' 
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {labels.useExisting}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setClientMode('new'); setSelectedClient(null); }}
                          className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                            clientMode === 'new' 
                              ? 'bg-white text-gray-900 shadow-sm' 
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {labels.createNew}
                        </button>
                      </div>
                      
                      {clientMode === 'select' && (
                        <div className="space-y-2">
                          {matchedClients.slice(0, 3).map((client) => (
                            <button
                              type="button"
                              key={client.id}
                              onClick={() => setSelectedClient(client)}
                              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                                selectedClient?.id === client.id
                                  ? 'border-gray-900 bg-gray-50'
                                  : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  client.type === 'COMPANY' ? 'bg-violet-100' : 'bg-gray-100'
                                }`}>
                                  {client.type === 'COMPANY' 
                                    ? <Building2 className="w-5 h-5 text-violet-600" /> 
                                    : <User className="w-5 h-5 text-gray-500" />
                                  }
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 truncate">
                                    {client.company_name || client.person_name}
                                  </div>
                                  <div className="text-sm text-gray-400 truncate">
                                    {client.email || client.contacts?.[0]?.email}
                                  </div>
                                </div>
                                {selectedClient?.id === client.id && (
                                  <div className="w-5 h-5 rounded-full bg-gray-900 flex items-center justify-center">
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* New client form */}
                  {(clientMode === 'new' || (!loadingMatches && matchedClients.length === 0)) && (
                    <div className="space-y-5">
                      {matchedClients.length === 0 && !loadingMatches && (
                        <p className="text-sm text-gray-400">{labels.noClientsFound}</p>
                      )}
                      
                      {/* Type toggle */}
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setNewClientType('PERSON')}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            newClientType === 'PERSON' 
                              ? 'border-gray-900 bg-gray-50' 
                              : 'border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <User className={`w-5 h-5 mb-2 ${newClientType === 'PERSON' ? 'text-gray-900' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${newClientType === 'PERSON' ? 'text-gray-900' : 'text-gray-500'}`}>
                            {labels.person}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewClientType('COMPANY')}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            newClientType === 'COMPANY' 
                              ? 'border-gray-900 bg-gray-50' 
                              : 'border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <Building2 className={`w-5 h-5 mb-2 ${newClientType === 'COMPANY' ? 'text-gray-900' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${newClientType === 'COMPANY' ? 'text-gray-900' : 'text-gray-500'}`}>
                            {labels.company}
                          </span>
                        </button>
                      </div>
                      
                      {newClientType === 'PERSON' ? (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={newClientData.person_name}
                            onChange={(e) => setNewClientData(prev => ({ ...prev, person_name: e.target.value }))}
                            placeholder={labels.fullName}
                            className="w-full px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all placeholder:text-gray-400"
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <input 
                              type="email" 
                              value={newClientData.email} 
                              onChange={(e) => setNewClientData(prev => ({ ...prev, email: e.target.value }))} 
                              placeholder={labels.email}
                              className="px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all placeholder:text-gray-400"
                            />
                            <input 
                              type="tel" 
                              value={newClientData.phone} 
                              onChange={(e) => setNewClientData(prev => ({ ...prev, phone: e.target.value }))} 
                              placeholder={labels.phone}
                              className="px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all placeholder:text-gray-400"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Brreg search */}
                          <div className="relative">
                            <Search className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                            <input
                              type="text"
                              value={brregSearch}
                              onChange={(e) => setBrregSearch(e.target.value)}
                              placeholder={labels.searchBrreg}
                              className="w-full pl-11 pr-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all placeholder:text-gray-400"
                            />
                            {brregLoading && (
                              <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute right-4 top-1/2 -translate-y-1/2" />
                            )}
                            
                            {brregResults.length > 0 && (
                              <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                                {brregResults.map((company) => (
                                  <button
                                    type="button"
                                    key={company.organisasjonsnummer}
                                    onClick={() => selectBrregCompany(company)}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                                  >
                                    <div className="font-medium text-gray-900">{company.navn}</div>
                                    <div className="text-xs text-gray-400 mt-0.5">Org.nr: {company.organisasjonsnummer}</div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <input 
                              type="text" 
                              value={newClientData.company_name} 
                              onChange={(e) => setNewClientData(prev => ({ ...prev, company_name: e.target.value }))} 
                              placeholder={labels.companyName}
                              className="px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all placeholder:text-gray-400"
                            />
                            <input 
                              type="text" 
                              value={newClientData.org_nr} 
                              onChange={(e) => setNewClientData(prev => ({ ...prev, org_nr: e.target.value }))} 
                              placeholder={labels.orgNr}
                              maxLength={9}
                              className="px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all font-mono placeholder:text-gray-400"
                            />
                          </div>
                          
                          {newClientData.address && (
                            <div className="flex items-center gap-2 text-sm text-gray-500 px-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {newClientData.address}, {newClientData.postal_code} {newClientData.city}
                            </div>
                          )}
                          
                          <div className="pt-4 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">{labels.contactPerson}</p>
                            <div className="grid grid-cols-2 gap-3">
                              <input 
                                type="text" 
                                value={newClientData.contact_name} 
                                onChange={(e) => setNewClientData(prev => ({ ...prev, contact_name: e.target.value }))} 
                                placeholder={labels.fullName}
                                className="px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all placeholder:text-gray-400"
                              />
                              <input 
                                type="email" 
                                value={newClientData.contact_email} 
                                onChange={(e) => setNewClientData(prev => ({ ...prev, contact_email: e.target.value }))} 
                                placeholder={labels.email}
                                className="px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all placeholder:text-gray-400"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ) : (
                /* STEP 2 */
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  {/* Client badge */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                        {(clientMode === 'select' && selectedClient?.type === 'COMPANY') || newClientType === 'COMPANY'
                          ? <Building2 className="w-4 h-4 text-gray-600" />
                          : <User className="w-4 h-4 text-gray-600" />
                        }
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{getClientName()}</div>
                        {clientMode === 'new' && (
                          <span className="text-xs text-gray-400">{labels.createNew}</span>
                        )}
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => setCurrentStep(1)} 
                      className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {labels.change}
                    </button>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                      {labels.matterTitle}
                    </label>
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={caseData.title}
                      onChange={(e) => setCaseData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder={labels.titlePlaceholder}
                      className="w-full px-4 py-3 text-base font-medium bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all placeholder:text-gray-300"
                    />
                  </div>

                  {/* Type & Priority row */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Type dropdown */}
                    <div ref={typeDropdownRef}>
                      <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                        {labels.type}
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                          className="w-full flex items-center justify-between px-4 py-3 text-sm bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-gray-700">{selectedType?.label}</span>
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {showTypeDropdown && (
                          <div className="absolute z-20 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                            {caseTypes.map((type) => (
                              <button
                                type="button"
                                key={type.value}
                                onClick={() => { setCaseData(prev => ({ ...prev, case_type: type.value })); setShowTypeDropdown(false); }}
                                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                                  caseData.case_type === type.value 
                                    ? 'bg-gray-900 text-white' 
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {type.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Priority */}
                    <div>
                      <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                        {labels.priority}
                      </label>
                      <div className="flex bg-gray-50 rounded-xl p-1">
                        {[
                          { value: 'LOW', label: labels.low },
                          { value: 'NORMAL', label: labels.normal },
                          { value: 'HIGH', label: labels.high },
                        ].map((p) => (
                          <button
                            type="button"
                            key={p.value}
                            onClick={() => setCaseData(prev => ({ ...prev, priority: p.value }))}
                            className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${
                              caseData.priority === p.value 
                                ? 'bg-white text-gray-900 shadow-sm' 
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                        {labels.description}
                      </label>
                      {caseData.description && (
                        <span className="flex items-center gap-1 text-xs text-violet-500">
                          <Sparkles className="w-3 h-3" />
                          {labels.aiSuggested}
                        </span>
                      )}
                    </div>
                    <textarea
                      value={caseData.description}
                      onChange={(e) => setCaseData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={labels.descPlaceholder}
                      rows={3}
                      className="w-full px-4 py-3 text-sm bg-gray-50 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-gray-900 outline-none transition-all resize-none placeholder:text-gray-400"
                    />
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                      {labels.tags}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {caseData.tags.map((tag, i) => (
                        <span 
                          key={i} 
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
                        >
                          {tag}
                          <button 
                            type="button" 
                            onClick={() => setCaseData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))} 
                            className="hover:text-gray-900 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <div className="relative">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newTag.trim()) {
                              e.preventDefault();
                              if (!caseData.tags.includes(newTag.trim())) {
                                setCaseData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
                              }
                              setNewTag('');
                            }
                          }}
                          placeholder={`+ ${labels.addTag}`}
                          className="px-3 py-1.5 text-xs bg-transparent border border-dashed border-gray-300 rounded-full outline-none focus:border-gray-400 w-24 placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Attachments hint */}
                  {attachments.length > 0 && (
                    <div className="text-xs text-gray-400 pt-2">
                      {attachments.length} {labels.attachmentsNote}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <div className="flex items-center justify-between">
              {currentStep === 1 ? (
                <>
                  <button 
                    type="button" 
                    onClick={onClose} 
                    className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    disabled={!isClientStepValid}
                    className="px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    {labels.continue}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <>
                  <button 
                    type="button" 
                    onClick={() => setCurrentStep(1)} 
                    className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1.5"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {labels.back}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isCreating || !isCaseStepValid}
                    className="px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {labels.creating}
                      </>
                    ) : (
                      labels.createMatter
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateCaseModal;
